import "./style.scss";
import React, { useEffect, useState, useRef, useCallback } from "react";
import Slider from "@material-ui/core/Slider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropImage";
import { Point, Area } from "react-easy-crop/types";

type Props = {
  className?: string;
  label: string;
  imageFormatName: string;
  onCrop: (image: string | null) => void;
  imageSource: string;
  uploadedImage: string;
  aspectRatio: number;
};

function ImageCrop({
  className = "",
  imageSource,
  uploadedImage,
  imageFormatName,
  label,
  aspectRatio = 4 / 3,
  onCrop,
}: Props) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentImage, setCurrentImage] = useState("");
  const baseUrl = useRef("");
  const baseFormat = useRef(".blob");
  const initialLoad = useRef(true);
  const cropperRef = useRef(null);
  const firstCropDone = useRef(false);
  const isMounted = useRef(true);

  const updateCurrentImage = (src: string) => {
    if (isMounted.current) {
      setCurrentImage(src);
    }
  };

  const srcSetVerify = async (urlVerify: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = `${urlVerify}?${Date.now()}`;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  const loadInitialImage = useCallback(
    async (urlVerify: string) => {
      const exists = await srcSetVerify(urlVerify);
      if (exists) {
        setTimeout(() => {
          updateCurrentImage(
            `${baseUrl.current}/${imageFormatName + baseFormat.current}?${Date.now()}`
          );
        }, 1000);
      } else {
        initialLoad.current = false;
        updateCurrentImage(imageSource);
      }
    },
    [imageFormatName, imageSource]
  );

  useEffect(() => {
    isMounted.current = true;

    if (imageSource) {
      baseUrl.current = imageSource.split("/").slice(0, -1).join("/");
      const imageFormatUrl = `${baseUrl.current}/${imageFormatName + baseFormat.current}`;
      loadInitialImage(imageFormatUrl);
    } else {
      initialLoad.current = false;
    }

    return () => {
      isMounted.current = false;
    };
  }, [imageFormatName, imageSource, loadInitialImage]);

  useEffect(() => {
    if (uploadedImage && isMounted.current) {
      setCurrentImage(uploadedImage);
    }
  }, [uploadedImage]);

  const onCropComplete = async (_: Area, croppedAreaPixels: Area) => {
    if (initialLoad.current || !currentImage || firstCropDone.current) return;

    firstCropDone.current = true;

    const croppedImage = await getCroppedImg(
      imageFormatName,
      aspectRatio,
      currentImage,
      croppedAreaPixels,
      0,
      { horizontal: false, vertical: false },
      cropperRef
    );

    onCrop(croppedImage);
  };

  const uploadCurrentImage = (input: HTMLInputElement) => {
    const file = input.files?.[0];
    if (file) {
      setCurrentImage(URL.createObjectURL(file));
    }
  };

  return (
    <div className="pctImageCropRoot">
      <p className="formatLabel">{label}</p>

      <div className={`pctImageCrop ${className}`}>
        {!currentImage && (
          <div className="loadingImage">
            <CircularProgress style={{ marginBottom: "80px" }} size={18} />
          </div>
        )}

        <div className="crop-container">
          <Cropper
            ref={cropperRef}
            image={currentImage}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            showGrid={true}
            onCropChange={(location: Point) => {
              initialLoad.current = false;
              firstCropDone.current = false;
              setCrop(location);
            }}
            onCropComplete={onCropComplete}
            onZoomChange={(value) => {
              setZoom(value);
              initialLoad.current = false;
              firstCropDone.current = false;
            }}
            onMediaLoaded={() => {
              setZoom(1);
              initialLoad.current = false;
              firstCropDone.current = false;
            }}
          />
        </div>

        <div className="cropFooter">
          <div className="controls">
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e, value) => {
                setZoom(Number(value));
                initialLoad.current = false;
                firstCropDone.current = false;
              }}
              classes={{ root: "slider" }}
            />
          </div>

          <label className="content-image-file-button btn">
            CARREGAR NOVA IMAGEM
            <input
              type="file"
              className="form-control-file"
              id="imagem"
              accept="image/png, image/jpeg, image/gif"
              onChange={(e) => uploadCurrentImage(e.target)}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

export default ImageCrop;
